import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  TextField,
  TableSortLabel,
  TableContainer,
  Button,
  Stack,
  InputLabel,
  Alert,
  FormControl,
  Select,
  MenuItem,
  Grid,
  Chip,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  TablePagination
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  AccountBalance as AccountBalanceIcon,
  Schedule as ScheduleIcon,
  Paid as PaidIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip as RechartsTooltip, PieChart, Pie, Cell, Legend, BarChart, Bar } from 'recharts';
import api from '../api';
import { useUser } from '../UserContext';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

function StakesSummary() {
  const { data: summary, isLoading, isError } = useQuery({
    queryKey: ['stakes', 'summary'],
    queryFn: async () => {
      const response = await api.get('/stakes/summary');
      return response.data;
    }
  });

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={2}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Failed to load stakes summary.
      </Alert>
    );
  }

  const {
    totalStaked = 0,
    activeStakes = 0,
    upcomingUnlocks = 0,
    totalRewards = 0,
    averageApy = 0
  } = summary || {};

  return (
    <Grid container spacing={3} sx={{ mb: 3 }}>
      <Grid item xs={12} sm={6} md={2.4}>
        <Card sx={{ height: '100%', bgcolor: 'background.paper' }}>
          <CardContent sx={{ textAlign: 'center', p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
              <AccountBalanceIcon sx={{ color: 'primary.main', mr: 1 }} />
              <Typography variant="subtitle2" color="text.secondary">
                Total Staked
              </Typography>
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              {Number(totalStaked).toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              MOANI
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={2.4}>
        <Card sx={{ height: '100%', bgcolor: 'background.paper' }}>
          <CardContent sx={{ textAlign: 'center', p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
              <TrendingUpIcon sx={{ color: 'success.main', mr: 1 }} />
              <Typography variant="subtitle2" color="text.secondary">
                Active Stakes
              </Typography>
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'success.main' }}>
              {activeStakes.toLocaleString()}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              stakes
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={2.4}>
        <Card sx={{ height: '100%', bgcolor: 'background.paper' }}>
          <CardContent sx={{ textAlign: 'center', p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
              <ScheduleIcon sx={{ color: 'warning.main', mr: 1 }} />
              <Typography variant="subtitle2" color="text.secondary">
                Unlocking (30d)
              </Typography>
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
              {Number(upcomingUnlocks).toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              MOANI
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={2.4}>
        <Card sx={{ height: '100%', bgcolor: 'background.paper' }}>
          <CardContent sx={{ textAlign: 'center', p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
              <PaidIcon sx={{ color: 'info.main', mr: 1 }} />
              <Typography variant="subtitle2" color="text.secondary">
                Total Rewards
              </Typography>
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'info.main' }}>
              {Number(totalRewards).toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              MOANI
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={2.4}>
        <Card sx={{ height: '100%', bgcolor: 'background.paper' }}>
          <CardContent sx={{ textAlign: 'center', p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '1.2rem' }}>
                📈
              </Typography>
              <Typography variant="subtitle2" color="text.secondary" sx={{ ml: 1 }}>
                Avg APY
              </Typography>
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>
              {Number(averageApy).toFixed(1)}%
            </Typography>
            <Typography variant="caption" color="text.secondary">
              weighted
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}

function StakeUnlockChart() {
  const { data: unlockData, isLoading, isError } = useQuery({
    queryKey: ['stakes', 'unlock-timeline'],
    queryFn: async () => {
      const response = await api.get('/stakes/unlock-timeline');
      return response.data;
    }
  });

  if (isLoading) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Stake Unlock Timeline</Typography>
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  if (isError) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Stake Unlock Timeline</Typography>
        <Alert severity="error">Failed to load unlock timeline.</Alert>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>Stake Unlock Timeline (Next 12 Months)</Typography>
      <ResponsiveContainer width="100%" height={400}>
        <BarChart data={unlockData || []}>
          <XAxis 
            dataKey="month" 
            tickFormatter={(value) => {
              const date = new Date(value);
              return date.toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
            }}
          />
          <YAxis 
            label={{ value: 'MOANI Amount', angle: -90, position: 'insideLeft' }}
            tickFormatter={(value) => value.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          />
          <RechartsTooltip 
            formatter={(value, name) => [
              Number(value).toLocaleString(undefined, { maximumFractionDigits: 0 }),
              name
            ]}
            labelFormatter={(label) => {
              const date = new Date(label);
              return date.toLocaleDateString(undefined, { 
                month: 'long', 
                year: 'numeric' 
              });
            }}
          />
          <Legend />
          <Bar 
            dataKey="oneMonth" 
            fill={COLORS[0]} 
            name="1 Month Stakes" 
          />
          <Bar 
            dataKey="twoMonths" 
            fill={COLORS[1]} 
            name="2 Month Stakes" 
          />
          <Bar 
            dataKey="threeMonths" 
            fill={COLORS[2]} 
            name="3 Month Stakes" 
          />
          <Bar 
            dataKey="sixMonths" 
            fill={COLORS[3]} 
            name="6 Month Stakes" 
          />
          <Bar 
            dataKey="total" 
            fill="#ff6b6b" 
            name="Total Unlocking" 
          />
        </BarChart>
      </ResponsiveContainer>
    </Paper>
  );
}

function StakingPeriodBreakdown() {
  const { data: breakdown, isLoading, isError } = useQuery({
    queryKey: ['stakes', 'period-breakdown'],
    queryFn: async () => {
      const response = await api.get('/stakes/period-breakdown');
      return response.data;
    }
  });

  if (isLoading) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Staking Period Breakdown</Typography>
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  if (isError) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Staking Period Breakdown</Typography>
        <Alert severity="error">Failed to load period breakdown.</Alert>
      </Paper>
    );
  }

  const pieData = (breakdown || []).map((item, index) => ({
    ...item,
    fill: COLORS[index % COLORS.length]
  }));

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>Staking Period Breakdown</Typography>
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie 
                data={pieData} 
                dataKey="totalAmount" 
                nameKey="period" 
                cx="50%" 
                cy="50%" 
                outerRadius={100} 
                label={({ period, percentage }) => `${period}: ${percentage}%`}
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <RechartsTooltip 
                formatter={(value) => [
                  Number(value).toLocaleString(undefined, { maximumFractionDigits: 0 }),
                  'MOANI'
                ]}
              />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </Grid>
        <Grid item xs={12} md={6}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Period</strong></TableCell>
                  <TableCell align="right"><strong>Count</strong></TableCell>
                  <TableCell align="right"><strong>Total Amount</strong></TableCell>
                  <TableCell align="right"><strong>Percentage</strong></TableCell>
                  <TableCell align="right"><strong>Avg APY</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(breakdown || []).map((row, index) => (
                  <TableRow key={row.period}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box 
                          sx={{ 
                            width: 12, 
                            height: 12, 
                            bgcolor: COLORS[index % COLORS.length], 
                            mr: 1,
                            borderRadius: '2px'
                          }} 
                        />
                        {row.period}
                      </Box>
                    </TableCell>
                    <TableCell align="right">{row.count}</TableCell>
                    <TableCell align="right">
                      {Number(row.totalAmount).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </TableCell>
                    <TableCell align="right">{row.percentage}%</TableCell>
                    <TableCell align="right">{Number(row.avgApy).toFixed(1)}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    </Paper>
  );
}

function StakeDetailsDialog({ open, onClose, stakeId }) {
  const { data: stakeDetails, isLoading } = useQuery({
    queryKey: ['stakes', 'details', stakeId],
    queryFn: async () => {
      if (!stakeId) return null;
      const response = await api.get(`/stakes/${stakeId}`);
      return response.data;
    },
    enabled: !!stakeId && open
  });

  const navigate = useNavigate();
  const { selectUserById } = useUser();


  if (!open) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Stake Details</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : stakeDetails ? (
          <Grid container spacing={3}>
            {/* Stake Information */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom color="primary">
                Stake Information
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Stake ID</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {stakeDetails.id}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Amount</Typography>
                  <Typography variant="h6" color="primary.main">
                    {Number(stakeDetails.amount).toLocaleString(undefined, { maximumFractionDigits: 0 })} MOANI
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Staking Period</Typography>
                  <Chip 
                    label={stakeDetails.stakingPeriod?.replace('_', ' ')} 
                    color="primary" 
                    size="small" 
                  />
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">APY at Staking</Typography>
                  <Typography variant="body1" color="success.main" fontWeight="bold">
                    {Number(stakeDetails.apyAtStaking).toFixed(2)}%
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                  <Chip 
                    label={stakeDetails.isActive ? 'Active' : 'Completed'} 
                    color={stakeDetails.isActive ? 'success' : 'default'} 
                    size="small" 
                  />
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Community Boost</Typography>
                  <Chip 
                    label={stakeDetails.hasCommunityBoost ? 'Yes' : 'No'} 
                    color={stakeDetails.hasCommunityBoost ? 'success' : 'default'} 
                    size="small" 
                  />
                </Box>
              </Stack>
            </Grid>

            {/* User Information */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom color="primary">
                User Information
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">User Name</Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      cursor: 'pointer', 
                      color: 'primary.main',
                      '&:hover': { textDecoration: 'underline' }
                    }}
                    onClick={() => {
                      if (stakeDetails?.userId) {
                        selectUserById(stakeDetails.userId, navigate);
                        onClose();
                      }
                    }}
                  >
                    {stakeDetails.user?.name || 'Unknown User'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                  <Typography variant="body2">
                    {stakeDetails.user?.email || 'N/A'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Wallet</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                    {stakeDetails.user?.wallet ? 
                      `${stakeDetails.user.wallet.slice(0, 8)}...${stakeDetails.user.wallet.slice(-6)}` : 
                      'N/A'
                    }
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">User Status</Typography>
                  <Chip 
                    label={stakeDetails.user?.status || 'Unknown'} 
                    color={stakeDetails.user?.status === 'ACTIVE' ? 'success' : 'warning'} 
                    size="small" 
                  />
                </Box>
              </Stack>
            </Grid>

            {/* Timeline Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom color="primary">
                Timeline & Rewards
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Start Date</Typography>
                    <Typography variant="body2">
                      {new Date(stakeDetails.startTime).toLocaleDateString(undefined, {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">End Date</Typography>
                    <Typography variant="body2">
                      {new Date(stakeDetails.endTime).toLocaleDateString(undefined, {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Days Remaining</Typography>
                    <Typography variant="body2" color={
                      stakeDetails.isActive ? 
                        (new Date(stakeDetails.endTime) > new Date() ? 'success.main' : 'error.main') : 
                        'text.secondary'
                    }>
                      {stakeDetails.isActive ? 
                        Math.max(0, Math.ceil((new Date(stakeDetails.endTime) - new Date()) / (1000 * 60 * 60 * 24))) : 
                        'Completed'
                      }
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Rewards Claimed</Typography>
                    <Typography variant="body2" color="info.main" fontWeight="bold">
                      {Number(stakeDetails.rewardsClaimed || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} MOANI
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        ) : (
          <Alert severity="error">Failed to load stake details.</Alert>
        )}
      </DialogContent>
      <DialogActions>
        {stakeDetails?.userId && (
          <Button 
            startIcon={<PersonIcon />} 
            onClick={() => {
              if (stakeDetails?.userId) {
                selectUserById(stakeDetails.userId, navigate);
                onClose();
              }
            }}
            variant="outlined"
          >
            View User
          </Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function StakesList() {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [orderBy, setOrderBy] = useState('createdAt');
  const [order, setOrder] = useState('desc');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [periodFilter, setPeriodFilter] = useState('all');
  const [selectedStakeId, setSelectedStakeId] = useState(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['stakes', 'list', page, rowsPerPage, orderBy, order, search, statusFilter, periodFilter],
    queryFn: async () => {
      const response = await api.get('/stakes', {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          orderBy,
          order,
          search,
          status: statusFilter !== 'all' ? statusFilter : undefined,
          period: periodFilter !== 'all' ? periodFilter : undefined
        }
      });
      return response.data;
    }
  });

  const navigate = useNavigate();
  const { selectUserById } = useUser();

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };


  const handleViewStake = (stakeId) => {
    setSelectedStakeId(stakeId);
  };

  if (isLoading) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>All Stakes</Typography>
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  if (isError) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>All Stakes</Typography>
        <Alert severity="error">Failed to load stakes list.</Alert>
      </Paper>
    );
  }

  const { stakes = [], totalCount = 0 } = data || {};

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>All Stakes ({totalCount.toLocaleString()})</Typography>
      
      {/* Filters */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
        <TextField
          label="Search by user name or email"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ minWidth: 250 }}
        />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Period</InputLabel>
          <Select
            value={periodFilter}
            label="Period"
            onChange={(e) => setPeriodFilter(e.target.value)}
          >
            <MenuItem value="all">All Periods</MenuItem>
            <MenuItem value="ONE_MONTH">1 Month</MenuItem>
            <MenuItem value="TWO_MONTHS">2 Months</MenuItem>
            <MenuItem value="THREE_MONTHS">3 Months</MenuItem>
            <MenuItem value="SIX_MONTHS">6 Months</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'user.name'}
                  direction={orderBy === 'user.name' ? order : 'asc'}
                  onClick={() => handleSort('user.name')}
                >
                  User
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={orderBy === 'amount'}
                  direction={orderBy === 'amount' ? order : 'desc'}
                  onClick={() => handleSort('amount')}
                >
                  Amount
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'stakingPeriod'}
                  direction={orderBy === 'stakingPeriod' ? order : 'asc'}
                  onClick={() => handleSort('stakingPeriod')}
                >
                  Period
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'startTime'}
                  direction={orderBy === 'startTime' ? order : 'desc'}
                  onClick={() => handleSort('startTime')}
                >
                  Start Date
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'endTime'}
                  direction={orderBy === 'endTime' ? order : 'desc'}
                  onClick={() => handleSort('endTime')}
                >
                  End Date
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={orderBy === 'apyAtStaking'}
                  direction={orderBy === 'apyAtStaking' ? order : 'desc'}
                  onClick={() => handleSort('apyAtStaking')}
                >
                  APY
                </TableSortLabel>
              </TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Rewards</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {stakes.map((stake) => (
              <TableRow key={stake.id} hover>
                <TableCell>
                  <Box>
                    <Typography 
                      component="a"
                      href={`/user/${stake.userId}/details`}
                      variant="body2" 
                      sx={{ 
                        cursor: 'pointer', 
                        color: 'primary.main',
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                      onClick={(event) => selectUserById(stake.userId, navigate)}
                      title="Click to view user details (Ctrl+click for new tab)"
                    >
                      {stake.user?.name || 'Unknown User'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {stake.user?.email || 'No email'}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight="bold">
                    {Number(stake.amount).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={stake.stakingPeriod?.replace('_', ' ')} 
                    size="small" 
                    color="primary"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {new Date(stake.startTime).toLocaleDateString()}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {new Date(stake.endTime).toLocaleDateString()}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" color="success.main">
                    {Number(stake.apyAtStaking).toFixed(1)}%
                  </Typography>
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5}>
                    <Chip 
                      label={stake.isActive ? 'Active' : 'Completed'} 
                      size="small" 
                      color={stake.isActive ? 'success' : 'default'}
                    />
                    {stake.hasCommunityBoost && (
                      <Chip 
                        label="Boost" 
                        size="small" 
                        color="secondary"
                      />
                    )}
                  </Stack>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" color="info.main">
                    {Number(stake.rewardsClaimed || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Tooltip title="View Details">
                    <IconButton 
                      size="small" 
                      onClick={() => handleViewStake(stake.id)}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />

      <StakeDetailsDialog 
        open={!!selectedStakeId} 
        onClose={() => setSelectedStakeId(null)} 
        stakeId={selectedStakeId} 
      />
    </Paper>
  );
}

export default function StakesManager() {
  const [tab, setTab] = useState(0);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
        <TrendingUpIcon sx={{ mr: 2, color: 'primary.main' }} />
        Stakes Management
      </Typography>
      
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="Overview" />
        <Tab label="All Stakes" />
      </Tabs>

      {tab === 0 && (
        <>
          <StakesSummary />
          <StakeUnlockChart />
          <StakingPeriodBreakdown />
        </>
      )}
      {tab === 1 && <StakesList />}
    </Box>
  );
}
